<script setup lang="ts">
import { useDeviceActions } from '@/stores/DeviceActions';
import { storeToRefs } from 'pinia';
import { useMutation } from '@urql/vue';
import { REGISTER_DEVICE, type Device } from '@/graphql/devices';
import { reactive, ref } from 'vue';
import { useToast } from 'bootstrap-vue-next';

const toast = useToast();

const registerForm = ref<HTMLFormElement | null>(null);
const serialNumber = ref<HTMLInputElement | null>(null);
const { isRegisterDeviceVisible } = storeToRefs(useDeviceActions());
const { executeMutation, fetching } = useMutation<{ registerDevice: Device }>(REGISTER_DEVICE);
const error = ref('');
const wasValidated = ref(false);

const registerInput = reactive({
  serialNumber: '',
  name: ''
});

function register() {
  const isValid = registerForm.value?.checkValidity();
  wasValidated.value = true;
  error.value = '';
  if (!isValid) {
    if (/\s/.test(registerInput.serialNumber)) {
      error.value = 'Whitespace characters are not allowed in serial numbers';
    }
    return;
  }
  executeMutation({
    input: registerInput
  }).then((result) => {
    if (result.error || !result.data?.registerDevice) {
      error.value = result.error?.message || 'Registration failed for unknown reason';
      return;
    }
    toast.show?.({ props: { variant: 'success', body: `Successfully created device ${registerInput.serialNumber}` } });
    isRegisterDeviceVisible.value = false;
  });
}

function close() {
  wasValidated.value = false;
  error.value = '';
  registerInput.name = '';
  registerInput.serialNumber = '';
}
</script>

<template lang="pug">
BModal#registerDevice(v-model="isRegisterDeviceVisible" title="Register Device" no-close-on-backdrop no-close-on-esc @hide="close")
  AlertBox(v-if="error" variant="danger") {{ error }}
  form.needs-validation(ref="registerForm" :class="{'was-validated': wasValidated}" no-validate @submit.prevent="false")
    .has-validation.mb-3
      .has-validation.mb-3
        label.form-label(for="register-device-serial") Serial Number
        input#register-device-serial.form-control(ref="serialNumber" v-model="registerInput.serialNumber" type="text" placeholder="Serial Number" pattern="^\\S+$" required)
      .has-validation.mb-3
        label.form-label(for="register-device-name") Name
        input#register-device-name.form-control(ref="name" v-model="registerInput.name" type="text" placeholder="Name")
  template(#footer)
    button.btn.btn-link(@click="isRegisterDeviceVisible = false") Cancel
    LaddaButton.btn.btn-primary(:is-loading="fetching" @click="register") Register Device
</template>
